import React, { DetailedHTMLProps, HTMLAttributes } from 'react';
import classnames from 'classnames';
import {
  cmxDateTime,
  caseUtils,
  conditionalUtils,
  objectUtils
} from '@codametrix/ui-common';
import { LoadingSlugs } from '@codametrix/ui-components';
import { launchAmplifyCase } from '../case-detail/window-open-args';
import { Conditional } from '@codametrix/ui-components';
import { CaseCard } from '../case-card/case-card';
import { CommonFilter } from '../common-filter/common-filter';
import './styles/index.scss';
const { isEqual, isEmpty } = objectUtils;
const { getChildWindowParams } = caseUtils;

type RelatedCaseItem = {
  caseRun: CMxCommonApp.RelatedCaseExtended;
  isCurrent: boolean;
};

export type RelatedCasesProps = {
  showLoading?: boolean;
  relatedCases?: CMxCommonApp.RelatedCaseExtended[];
  activeItem: CMxAPI.CaseRun;
  onOpenRelatedCase: (windowOpenArgs: CMxCommonApp.WindowOpenArguments) => void;
  showProviderInfo: (providerInfo: CMxCommonApp.ProviderSearchParams) => void;
  filterData: CMxCommonApp.SharedFilter[];
  relatedCasesFilterSelection: CMxCommonApp.FilterSelection;
  onRelatedCasesFilterApply: (filters: CMxCommonApp.FilterSelection) => void;
  relatedCasesFilterCapability: CMxCommonApp.Capability;
} & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const RelatedCases: React.FunctionComponent<RelatedCasesProps> = ({
  relatedCases = [],
  showLoading = false,
  activeItem,
  onOpenRelatedCase,
  showProviderInfo,
  filterData = [],
  relatedCasesFilterSelection,
  onRelatedCasesFilterApply,
  relatedCasesFilterCapability,
  ...rest
}) => {
  const styleClasses = [
    'related-cases',
    {
      'bg-loaded': !showLoading,
      'bg-with-cases': !!relatedCases?.length,
      'bg-empty': !showLoading,
      'bg-show-loading': showLoading
    }
  ];

  const handleRowSelection = (
    relatedCase: CMxCommonApp.RelatedCaseExtended
  ) => {
    if (!!relatedCase) {
      const childWindowParams: CMxCommonApp.WindowOpenArguments =
        getChildWindowParams(
          relatedCase?.case_uid,
          relatedCase?.process_instance_guid,
          relatedCase?.service_line,
          launchAmplifyCase,
          relatedCase?.case_id
        );
      onOpenRelatedCase(childWindowParams);
    }
  };

  // Preparing the case list to identify the current case run
  const relatedCaseList = relatedCases.map((caseRun) => {
    const row = {
      isCurrent:
        caseRun.case_id === activeItem?.case_id &&
        caseRun.process_instance_guid === activeItem?.process_instance_guid,
      id: caseRun.case_id + '-' + caseRun.process_instance_guid,
      caseRun: {
        ...caseRun,
        date_of_service: cmxDateTime.format(
          caseRun.date_of_service,
          cmxDateTime.FORMATS.DATE_OF_SERVICE
        )
      }
    };
    return row;
  });

  const showRelatedCasesFilter = conditionalUtils.capabilityValue(
    relatedCasesFilterCapability?.on ?? false
  );
  let defaultFilterSelection = {};
  filterData &&
    filterData?.forEach((filter) => {
      const defaults: { [key: string]: boolean } = {};
      filter?.filterOptions?.forEach((item) => {
        defaults[item?.key] = item?.defaultSelection === 'INCLUDE';
      });
      defaultFilterSelection[filter.type] = defaults;
    });

  const isDefaultFilterStateChanged =
    !isEmpty(relatedCasesFilterSelection) &&
    !isEqual(defaultFilterSelection, relatedCasesFilterSelection);

  return (
    <div className={classnames(styleClasses)} {...rest}>
      <Conditional every={[!!filterData?.length, showRelatedCasesFilter]}>
        <CommonFilter
          filterData={filterData}
          relatedCasesFilterSelection={relatedCasesFilterSelection}
          onApply={onRelatedCasesFilterApply}
        />
      </Conditional>
      <Conditional every={[!showLoading, !relatedCaseList?.length]}>
        <span className="empty-content">
          {isDefaultFilterStateChanged
            ? 'With the current filters applied, there are no results display.'
            : 'With the default filters applied, there are no results to display.'}
        </span>
      </Conditional>

      <Conditional every={[showLoading]}>
        <div className="loading-container" data-testid="related-cases-loading">
          <LoadingSlugs key="loading-slugs" numberItems={3} />
        </div>
      </Conditional>

      <Conditional
        every={[!showLoading, !!relatedCaseList?.length]}
        displayMode="flex">
        <div className="related-table">
          <div className="grid-container">
            {relatedCaseList?.map((relatedCase, idx) => {
              return (
                <CaseCard
                  key={idx}
                  caseData={relatedCase.caseRun}
                  isCurrent={relatedCase.isCurrent}
                  onCaseClick={handleRowSelection}
                  showProviderInfo={showProviderInfo}
                  currentCaseTenantCode={activeItem?.facility}
                  currentCaseTenantName={activeItem?.tenant_name}
                />
              );
            })}
          </div>
        </div>
      </Conditional>
    </div>
  );
};

RelatedCases.displayName = 'RelatedCases';

export { RelatedCases };
